import React from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import Select, {
  IProps as ISelectProps,
  IValue,
  IValues,
} from './_module/Select';
import {
  companyQuery,
  ICompanyQueryData,
  ICompanyVars,
  ICompanyVarsPartial,
} from '../../../graphql/query/company/useCompanyQuery';
import useLanguage from '../_hook/useLanguage';
import SchemaCompanyLogoAvatar from '../_schema/SchemaCompanyLogoAvatar';

interface IProps<TValue extends IValue | IValues> extends ISelectProps<TValue> {
  queryVariableSort?: ICompanyVars['sort'];
  useCompanyLogo?: boolean;
  defaultOptions?: IValues;
  useSearch?: boolean;
}

export default function SelectSearchCompany<TValue extends IValue | IValues>(
  props: IProps<TValue>
) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const { getText } = useLanguage();
  const {
    creatable,
    disabled,
    label,
    onChange,
    placeholder,
    value,
    menuPosition,
    required,
    margin,
    error,
    fullWidth,
    helperText,
    isMulti,
    queryVariableSort,
    useCompanyLogo,
    defaultOptions,
    useSearch,
  } = props;

  return (
    <Select<TValue>
      creatable={creatable}
      isMulti={isMulti}
      async
      isClearable
      loadOptions={async (input) => {
        const { data } = await client.query<
          ICompanyQueryData,
          ICompanyVarsPartial
        >({
          query: companyQuery(`
                _id
                name {
                  la
                  text
                }
               
                ${
                  useCompanyLogo
                    ? `
                  media {
                      images {
                          logo {
                            status 
                            filename
                            filename_small
                            filenameUrl
                            filenameSmallUrl
                          }
                      }
                  }
                  `
                    : ''
                }
              `),
          variables: {
            search: {
              name: input,
            },
            sort: queryVariableSort || {},
            limit: 30,
            useSearch,
          },
        });

        if (data && data.companies) {
          return [
            ...(defaultOptions || []),
            ...data.companies.items.map((company) => ({
              label: getText(company.name),
              value: company._id,
              startComponent: useCompanyLogo ? (
                <SchemaCompanyLogoAvatar
                  width={3}
                  height={3}
                  company={company}
                />
              ) : undefined,
            })),
          ];
        }

        return defaultOptions || [];
      }}
      menuPosition={menuPosition}
      label={label}
      required={required}
      margin={margin}
      error={error}
      disabled={disabled}
      fullWidth={fullWidth}
      helperText={helperText}
      value={value}
      onChange={onChange}
      placeholder={
        placeholder || t('enter__type__', { type: t('company_name') })
      }
    />
  );
}
